import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FeatureFlag } from '@app/enums';
import { Schedule } from '@app/models/payroll/schedule.model';
import { PayrollResources } from '@app/modules/payroll/payroll.resources';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { FilterPayrollReportForm } from '@forms/reporting/components';
import { Office } from '@models/company/office.model';
import { WorkersCompensationPerLineItemReportService } from '@payroll/services';
import { ReportFilter } from '@reporting/interfaces/report-filter.interface';
import { EnhancedPayrollReportingService } from '@reporting/services';
import { Report } from '@reporting/services/queries/report';
import moment from 'moment';
import stickybits from 'stickybits';
import { WorkersCompensationReporting } from '../../reports/enhanced-reports';

@Component({
    templateUrl: './workers-compensation-report.template.html',
    styleUrls: ['./workers-compensation-report.style.scss'],
    providers: [WorkersCompensationPerLineItemReportService],
})
export class WorkersCompensationReportView implements OnInit {
    @ViewChild('filterForm', { static: true }) filterForm: FilterPayrollReportForm;

    startAt: Date | null = null;
    endAt: Date | null = null;
    schedule: Schedule | null = null;
    isSingleDayRange = false;

    isLoading = true;
    includePending = true;

    report: Report | null = null;
    reportInterface = WorkersCompensationReporting;
    stickyColumns: (string | number)[];
    lastStickyColumn: string | number;
    displayBackRoute = true;
    jurisdictions: { label: string; value: string }[];
    jurisdiction = '';

    constructor(
        private auth: AuthService,
        private http: HttpClient,
        private notify: NotifyService,
        private reportingService: EnhancedPayrollReportingService,
        private perLineItemReport: WorkersCompensationPerLineItemReportService,
        private featureService: FeatureService
    ) {}

    async ngOnInit(): Promise<void> {
        this.getValidJurisdictions().then((jurisdictions) => (this.jurisdictions = jurisdictions));

        this.loadSchedule()
            .then(() => this.display())
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err.message || 'reporting.payroll.couldNotLoadReport');
            });
    }

    onFilter(dates: { startAt: Date; endAt: Date }, includePending: boolean, jurisdiction: string): void {
        if (
            dates.startAt == this.startAt &&
            dates.endAt == this.endAt &&
            includePending == this.includePending &&
            jurisdiction == this.jurisdiction
        ) {
            return;
        }

        this.setDates(dates.startAt, dates.endAt);
        this.includePending = includePending;
        this.jurisdiction = jurisdiction;
        this.display();
    }

    exportCSV(): void {
        this.isLoading = true;
        this.reportingService.exportCSV(this.report, this.getReportFilters()).finally(() => (this.isLoading = false));
    }

    async exportPerLineItemCSV(): Promise<void> {
        this.isLoading = true;

        try {
            await this.perLineItemReport.fetch(this.getReportFilters());
        } finally {
            this.isLoading = false;
        }
    }

    trackByFn(index: number): number {
        return index;
    }

    protected sticky(): void {
        setTimeout(() => {
            stickybits('.left-sticky');
            stickybits('.top-sticky');
        });
    }

    private async display(): Promise<void> {
        this.isLoading = true;

        try {
            this.report = await this.reportingService.display(this.reportInterface, this.getReportFilters());
            this.stickyColumns = this.report.sheet.columns
                .filter((column) => column.sticky)
                .map((column) => column.key);
            this.lastStickyColumn = this.stickyColumns[this.stickyColumns.length - 1];
            this.sticky();
        } catch (err) {
            this.notify.error(err.message || 'reporting.payroll.loadedWithErrors');
        }

        this.isLoading = false;
    }

    private async loadSchedule(): Promise<void> {
        try {
            const res = await this.http
                .get(PayrollResources.Schedule.replace(':company', this.auth.company.id.toString()))
                .toPromise();
            this.schedule = new Schedule(res['data']['attributes']);
            const [startAt, endAt] = this.reportInterface.getInitialDates(this.schedule);
            this.setDates(startAt, endAt);
        } catch (err) {
            this.notify.error(err.message || 'reporting.payroll.couldNotLoadSchedule');
            this.isLoading = false;
        }
    }

    private getReportFilters(): ReportFilter[] {
        return [
            { name: 'from', value: moment(this.startAt).format('YYYY-MM-DD') },
            { name: 'to', value: moment(this.endAt).format('YYYY-MM-DD') },
            { name: 'include_pending_payrolls', value: this.includePending ? '1' : '0' },
            { name: 'jurisdiction', value: this.jurisdiction },
        ];
    }

    private setDates(startAt: Date, endAt: Date): void {
        this.isSingleDayRange =
            moment(startAt).isSame(endAt, 'day') || moment(startAt).add(1, 'day').isSame(endAt, 'day');

        this.startAt = startAt;
        this.endAt = endAt;
    }

    private async getValidJurisdictions(): Promise<{ label: string; value: string }[]> {
        const [offices] = await Office.param('company', this.auth.company.id).orderBy('province', 'ASC').all();
        const hasQuebec = await this.featureService.has(FeatureFlag.qcPayroll);
        const uniqueOffices = new Set<string>(
            offices
                .filter(
                    //NT and NU do not have workers compensation
                    // show QC if on QC
                    (office) =>
                        office.province !== 'NT' && office.province !== 'NU' && (office.province !== 'QC' || hasQuebec)
                )
                .map((office: Office) => office.province)
        );

        return [
            { label: 'All', value: '' },
            ...Array.from(uniqueOffices).map((province) => ({ label: province, value: province })),
        ];
    }
}
